import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import MyCollection from "./MyCollection";
import Top10 from "../../Top10/Top10";
import CollectionStats from "../../CollectionStats/CollectionStats";
import GridViewIcon from "@mui/icons-material/GridView";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LineAxisIcon from "@mui/icons-material/LineAxis";
import SearchIcon from "@mui/icons-material/Search";
import { Team } from "../../../services/shirts/types";
import { Shirt } from "../../../entities/Shirt";
import { User } from "../../../entities/User";
import JoinPremiumPopUpNew from "../../../components/JoinPremiumPopUpNew/JoinPremiumPopUpNew";

export enum COLLECTION_SECTION {
  OVERVIEW = "overview",
  TOP10 = "top10",
  STATS = "stats",
}

interface CollectionProps {
  teamOptions: Array<Team>;
  setTeam: Function;
  lastPage: number | undefined;
  page: number;
  setPage: Function;
  shirts: Array<Shirt>;
  setOrderBy: Function;
  selectedTab: COLLECTION_SECTION;
  parentPath: string;
  itemsPerPage: number;
  setItemsPerPage: Function;
  user?: User;
  ownPage: boolean;
}

const Collection = ({
  teamOptions,
  setTeam: setTeamFilter,
  lastPage,
  page,
  setPage,
  shirts,
  setOrderBy,
  selectedTab,
  parentPath,
  itemsPerPage,
  setItemsPerPage,
  user,
  ownPage,
}: CollectionProps) => {
  const [team, setTeam] = useState<Team | "allTeam">("allTeam");
  const navigate = useNavigate();

  const setSelectedTab = (targetTab: COLLECTION_SECTION) => {
    const targetUrl = parentPath + "/" + targetTab;
    navigate(targetUrl);
  };

  useEffect(() => {
    if (team !== "allTeam") {
      setTeamFilter(team !== null ? team.id : undefined);
    } else {
      setTeamFilter("allTeam");
    }
  }, [team, setTeamFilter]);

  // Check if viewing someone else's private collection
  const isViewingPrivateCollection = !ownPage && user?.collection?.private;

  return (
    <>
      <Box
        className={"shadow-box"}
        sx={{
          display: { xs: "block", sm: "flex" },
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: "10px",
          backgroundColor: "white",
          width: "100%",
          height: { xs: "fit-content", sm: "65px" },
          padding: { xs: "12px 1rem", sm: "0px 1rem" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: { xs: "center", sm: "start" },
            alignItems: "center",
            columnGap: 1,
          }}
        >
          <Button
            startIcon={<GridViewIcon />}
            variant={
              selectedTab === COLLECTION_SECTION.OVERVIEW
                ? "customTextSelected"
                : "customText"
            }
            onClick={() => setSelectedTab(COLLECTION_SECTION.OVERVIEW)}
          >
            Overview
          </Button>
          <Button
            startIcon={<StarBorderIcon />}
            variant={
              selectedTab === COLLECTION_SECTION.TOP10
                ? "customTextSelected"
                : "customText"
            }
            onClick={() => setSelectedTab(COLLECTION_SECTION.TOP10)}
          >
            Top 10
          </Button>
          <Button
            startIcon={<LineAxisIcon />}
            variant={
              selectedTab === COLLECTION_SECTION.STATS
                ? "customTextSelected"
                : "customText"
            }
            onClick={() => setSelectedTab(COLLECTION_SECTION.STATS)}
          >
            Stats
          </Button>
        </Box>
        <Box
          sx={{
            justifyContent: "end",
            alignItems: "center",
            width: { xs: "100%", sm: "320px", md: "250px" },
            display:
              selectedTab === COLLECTION_SECTION.OVERVIEW ? "flex" : "none",
            marginTop: { xs: "10px", sm: "0px" },
          }}
        >
          <Box
            sx={{
              display: { xs: "flex", md: "block" },
              width: "100%",
              columnGap: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                fullWidth
                getOptionLabel={(option: Team) => option.name}
                options={teamOptions}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="All Clubs"
                    InputLabelProps={{
                      sx: {
                        color: "#3c3c40",
                        fontSize: "14px",
                        fontWeight: 700,
                        marginTop: "2px",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& input": {
                          padding: "4px !important",
                        },
                      },
                    }}
                  />
                )}
                value={team === "allTeam" ? undefined : team}
                onChange={(event: any, newValue: Team | null) => {
                  if (newValue) {
                    setTeam(newValue);
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, display: { xs: "block", md: "none" } }}>
              <TextField
                // value={text}
                // onChange={(e) => setText(e.target.value)}
                placeholder="Search"
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        // onClick={handleSearch}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    paddingRight: "0px !important",
                    "& input": {
                      padding: "10px !important",
                    },
                    "& input::placeholder": {
                      color: "#3c3c40",
                      fontSize: "14px",
                      fontWeight: 700,
                      marginTop: "2px",
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ marginTop: "1.5rem" }}>
        {isViewingPrivateCollection ? (
          <Box
            className={"shadow-box"}
            sx={{
              borderRadius: "10px",
              backgroundColor: "white",
              padding: "2rem",
            }}
          >
            <JoinPremiumPopUpNew
              title="This collection is private"
              text="This user has chosen to keep their collection private. Only VIP members can make their collections private. Do you want to become a VIP too?"
            />
          </Box>
        ) : (
          <>
            {selectedTab === COLLECTION_SECTION.OVERVIEW && (
              <MyCollection
                setOrderBy={setOrderBy}
                lastPage={lastPage}
                page={page}
                setPage={setPage}
                shirts={shirts}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
              />
            )}
            {selectedTab === COLLECTION_SECTION.TOP10 && <Top10 />}
            {selectedTab === COLLECTION_SECTION.STATS && (
              <CollectionStats
                path={parentPath + "/" + COLLECTION_SECTION.STATS}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default Collection;
